import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { formatCurrency } from './helpers';

export const CrossChargesTab = ({ crossCharges }) => {
  const navigate = useNavigate();

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <CardTitle className='text-xl font-bold text-slate-800'>
          Cross Charges
          <span className='ml-2 text-lg font-normal text-slate-500'>
            Total Moved: {formatCurrency(crossCharges?.outgoingTotal || 0)}
          </span>
        </CardTitle>
        <p className='text-sm text-slate-500 mt-1'>
          Labor charges that were originally charged to this job but moved to another purchase order.
          Use this as reference when the original PO was exhausted and charges had to be redistributed.
        </p>
      </CardHeader>
      <CardContent>
        {crossCharges?.outgoingCount > 0 && (
          <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm'>
            <p className='text-sm text-amber-800'>
              <AlertTriangle className='w-4 h-4 inline mr-1' />
              <strong>{crossCharges.outgoingCount}</strong> labor {crossCharges.outgoingCount === 1 ? 'entry was' : 'entries were'} moved
              from this job totaling <strong>{formatCurrency(crossCharges.outgoingTotal)}</strong>.
              These charges are no longer included in this job's totals.
            </p>
          </div>
        )}
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-sm' data-testid='cross-charges-table'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Date</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Crew Member</th>
                <th className='text-right p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Hours</th>
                <th className='text-right p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Amount</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Moved To</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Reason</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Date Moved</th>
              </tr>
            </thead>
            <tbody>
              {(crossCharges?.outgoing || []).map((cc) => (
                <tr key={cc.id} className='border-b border-slate-200 hover:bg-slate-50'>
                  <td className='p-2 font-mono text-slate-600'>{cc.date || '-'}</td>
                  <td className='p-2 font-medium text-slate-700'>{cc.crewMemberName}</td>
                  <td className='p-2 text-right font-mono text-slate-700'>{cc.totalHours || '-'}</td>
                  <td className='p-2 text-right font-mono font-bold text-red-600'>{formatCurrency(cc.amount)}</td>
                  <td className='p-2'>
                    <button className='text-blue-600 hover:underline font-medium'
                      onClick={() => navigate(`/dashboard/job/${cc.targetJobId}`)}>
                      {cc.targetJobNumber}
                    </button>
                    {cc.targetJobDescription && (
                      <span className='text-xs text-slate-400 block'>
                        {cc.targetJobDescription.substring(0, 40)}{cc.targetJobDescription.length > 40 ? '...' : ''}
                      </span>
                    )}
                  </td>
                  <td className='p-2'>
                    <Badge variant='outline' className={`text-xs rounded-sm ${
                      cc.reason?.includes('Correction') ? 'border-slate-300 text-slate-700' : 'border-amber-300 text-amber-700 bg-amber-50'
                    }`}>
                      {cc.reason?.includes('Correction') ? 'Correction' : 'Cross Charge'}
                    </Badge>
                  </td>
                  <td className='p-2 font-mono text-xs text-slate-500'>{cc.createdAt?.split('T')[0] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!crossCharges?.outgoing || crossCharges.outgoing.length === 0) && (
            <div className='text-center p-8 text-slate-500'>No cross charges for this job</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
